import { TIME_GUTTER_CLASS } from '../constants';
import type { EventInstance } from '../types';
import { getEventColor, layoutSpanningBars } from '../utils';

interface AllDayRowProps {
  dayColumns: { date: Date; events: EventInstance[] }[];
  onClickEvent: (event: EventInstance) => void;
  eventColorPriority?: 'event' | 'calendar';
  /** Matches the time grid's reserved scrollbar gutter — see useScrollbarWidth. */
  scrollbarGutterPx?: number;
}

/** Bar height + gap, in px. Kept here because the row's min-height derives from it. */
const LANE_HEIGHT_PX = 18;
/**
 * How many lanes the band will grow to before it stops.
 *
 * The per-column version capped at 3 chips with a "+N more" line; converting to
 * bars dropped the cap, so a week with many overlapping all-day entries grew an
 * unbounded band and pushed the hour grid off-screen. Same guarantee as before,
 * expressed in lanes.
 */
const MAX_LANES = 3;

/**
 * The all-day band above the time grid.
 *
 * Renders multi-day entries as ONE CONTINUOUS BAR across the days they cover,
 * which is what Google, Apple and Outlook all do — verified against Google for
 * a Mon 9am -> Wed 5pm event, which it draws as a single bar in month, week and
 * day, carrying just the title.
 *
 * This used to render a separate chip per day column, so a three-day booking
 * appeared as three independent events. The columns are already a CSS grid, so
 * a bar is just a child placed with `grid-column: start / span n` — no
 * percentage math, and it stays aligned with the day columns for free.
 *
 * Timed events longer than 24h belong here too, not in the time grid below
 * (`isMultiDayBarEvent`); a booking spanning days is not a block you can
 * meaningfully draw against an hour axis.
 */
export function AllDayRow({
  dayColumns,
  onClickEvent,
  eventColorPriority = 'event',
  scrollbarGutterPx = 0,
}: AllDayRowProps) {
  const days = dayColumns.map((c) => c.date);
  // Deduplicate: an event covering several columns appears in each column's
  // list, and laying out the same instance repeatedly would stack copies of one
  // bar into separate lanes.
  const seen = new Set<string>();
  const events: EventInstance[] = [];
  for (const col of dayColumns) {
    for (const e of col.events) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      events.push(e);
    }
  }

  const allBars = layoutSpanningBars(events, days);
  if (allBars.length === 0) return null;

  const bars = allBars.filter((b) => b.lane < MAX_LANES);
  const laneCount = bars.reduce((max, b) => Math.max(max, b.lane + 1), 0);

  // How many hidden bars touch each column, so the overflow note is per-day
  // rather than a single count for the whole week.
  const hiddenPerDay = days.map(
    (_, i) =>
      allBars.filter((b) => b.lane >= MAX_LANES && i >= b.startIndex && i < b.startIndex + b.span)
        .length,
  );

  return (
    <div className="border-b flex" style={{ paddingRight: scrollbarGutterPx }}>
      <div
        className={`${TIME_GUTTER_CLASS} text-[10px] text-muted-foreground text-right pr-2 py-1`}
      >
        all-day
      </div>

      <div className="flex-1 min-w-0 relative">
        {/* Column separators, drawn under the bars so a bar can cross them. */}
        <div
          className="absolute inset-0 grid"
          style={{ gridTemplateColumns: `repeat(${dayColumns.length}, minmax(0, 1fr))` }}
          aria-hidden="true"
        >
          {dayColumns.map((_, i) => (
            <div key={i} className="border-r last:border-r-0" />
          ))}
        </div>

        <div
          className="relative grid py-1 px-0.5 gap-y-0.5"
          style={{
            gridTemplateColumns: `repeat(${dayColumns.length}, minmax(0, 1fr))`,
            gridAutoRows: `${LANE_HEIGHT_PX - 2}px`,
            minHeight: Math.max(
              28,
              (laneCount + (hiddenPerDay.some((n) => n > 0) ? 1 : 0)) * LANE_HEIGHT_PX + 8,
            ),
          }}
        >
          {bars.map((bar) => (
            <button
              key={bar.event.id}
              type="button"
              className={`min-w-0 truncate text-[10px] px-1 py-0.5 cursor-pointer hover:opacity-80 text-white text-left ${
                bar.continuesBefore ? 'rounded-l-none' : 'rounded-l'
              } ${bar.continuesAfter ? 'rounded-r-none' : 'rounded-r'}`}
              style={{
                gridColumn: `${bar.startIndex + 1} / span ${bar.span}`,
                gridRow: bar.lane + 1,
                backgroundColor: getEventColor(bar.event, eventColorPriority),
              }}
              onClick={() => onClickEvent(bar.event)}
              title={bar.event.title}
            >
              {/* A bar running in from an earlier week/day repeats the title
                  rather than showing a bare colour block — the row it started
                  on may not be on screen. */}
              {bar.continuesBefore ? `◀ ${bar.event.title}` : bar.event.title}
            </button>
          ))}
          {hiddenPerDay.map((n, i) =>
            n > 0 ? (
              <button
                key={`more-${i}`}
                type="button"
                className="min-w-0 truncate px-1 text-left text-[10px] text-muted-foreground hover:underline"
                style={{ gridColumn: `${i + 1} / span 1`, gridRow: MAX_LANES + 1 }}
                onClick={() => {
                  const first = allBars.find(
                    (b) => b.lane >= MAX_LANES && i >= b.startIndex && i < b.startIndex + b.span,
                  );
                  if (first) onClickEvent(first.event);
                }}
              >
                +{n} more
              </button>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
}
